import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { useTranslation } from '@hooks';
import { useModal } from '@context/ModalContext';
import { useAccount } from '@context/AccountContext';
import { SharedServices } from '@services';
import staticMenuItems from '@lib/menuItems';
import { Logo } from '@components/common';
import LeftMenuModal from 'components/common/Nav/LeftMenuModal';
import MenuItem from '@components/common/Nav/MenuItem';
import { Caption } from '@components/styles/Typography';
import {
    CloseIcon,
    FavoriteIcon,
    InstagramIcon,
    OrderIcon,
    TicketIcon,
} from '@components/icons';
import { sizes } from '../../../styles/variables';
import FacebookIcon from '../../../public/facebookIcon.png';

const LeftMenu = ({ name, email }) => {
    const router = useRouter();
    const { closeLeftMenuModal } = useModal();
    const { currentUser } = useAccount();
    const { t, locale } = useTranslation();
    const { data, isLoading } = useQuery({
        queryKey: ['menuItems'],
        queryFn: SharedServices.getMenuItems,
    });

    const menuItems = isLoading ? staticMenuItems : data?.data;
    const [isSubMenuOpen, setIsSubMenuOpen] = useState({});

    const openSubMenu = (item) => {
        if (item.id === isSubMenuOpen.id) {
            setIsSubMenuOpen({ ...isSubMenuOpen, open: !isSubMenuOpen.open });
        } else {
            setIsSubMenuOpen({ ...isSubMenuOpen, open: false });
            setIsSubMenuOpen({
                id: item.id,
                open: true,
            });
        }
    };

    const slugWithQueries = (sort, slug) => {
        if (sort !== 'newProduct' && sort !== undefined) {
            return `/${router.locale}${slug}?order=${sort}`;
        }
        return `/${router.locale}${slug}`;
    };
    const [activeItem, setActiveItem] = useState();
    useEffect(() => {
        if (menuItems) {
            setActiveItem(null);
            menuItems.forEach((item) => {
                if (item?.child?.length > 0) {
                    item.child.forEach((child) => {
                        if (child?.data?.length > 0) {
                            child.data.forEach((data) => {
                                if (
                                    slugWithQueries(
                                        data?.sort,
                                        `/${data?.slug[locale]}`,
                                    ) === `/${router.locale}${router.asPath}`
                                ) {
                                    setActiveItem(data?.parent);
                                }
                            });
                        }
                    });
                }
            });
        }
    }, [router, router.pathname, menuItems]);

    return (
        <LeftMenuModal>
            <MenuHeader>
                <Logo width="auto" />
                <CloseIconWrapper onClick={closeLeftMenuModal}>
                    <CloseIcon />
                </CloseIconWrapper>
            </MenuHeader>
            <MenuContext>
                <CategoryItemsWrapper>
                    {menuItems?.map((item) =>
                        // eslint-disable-next-line react/jsx-no-useless-fragment
                        item.child && item.child.length > 0 ? (
                            <React.Fragment key={item?.id}>
                                <ParentWrapper
                                    onClick={() => openSubMenu(item)}
                                    key={item?.id}
                                >
                                    <Caption
                                        size={sizes.xs}
                                        lineHeight="22px"
                                        letterSpacing={
                                            (isSubMenuOpen.id === item.id &&
                                                isSubMenuOpen.open === true) ||
                                            activeItem === item?.id
                                                ? '2px'
                                                : '1px'
                                        }
                                        fontFamily={
                                            ((isSubMenuOpen.id === item.id &&
                                                isSubMenuOpen.open === true) ||
                                                activeItem === item?.id) &&
                                            'Futura Now Headline Medium'
                                        }
                                        color="primary"
                                    >
                                        {item?.name[locale]}
                                    </Caption>
                                </ParentWrapper>
                                {isSubMenuOpen.id === item.id &&
                                    isSubMenuOpen.open === true && (
                                        <>
                                            {item.child.map((child, c) => (
                                                <SubCategory
                                                    key={`sub-category-group-${c.toString()}`}
                                                >
                                                    {child?.group_name && (
                                                        <SubCategoryTitle>
                                                            <Caption
                                                                fontFamily="Futura Now Headline Medium"
                                                                color="primary"
                                                            >
                                                                {
                                                                    child
                                                                        ?.group_name[
                                                                        locale
                                                                    ]
                                                                }
                                                            </Caption>
                                                        </SubCategoryTitle>
                                                    )}
                                                    {child.name && (
                                                        <MenuItem
                                                            hideProductCount
                                                            key={child?.id}
                                                            count={
                                                                child?.productCount
                                                            }
                                                            id={child?.id}
                                                            name={
                                                                child?.name?.[
                                                                    locale
                                                                ]
                                                            }
                                                            slug={`/${child?.slug[locale]}`}
                                                            sort={child?.sort}
                                                            isSub
                                                        />
                                                    )}

                                                    {child?.data &&
                                                        child?.data.length >
                                                            0 &&
                                                        child?.data.map(
                                                            (category) => (
                                                                <MenuItem
                                                                    hideProductCount
                                                                    key={
                                                                        category?.id
                                                                    }
                                                                    count={
                                                                        category?.productCount
                                                                    }
                                                                    id={
                                                                        category?.id
                                                                    }
                                                                    name={
                                                                        category
                                                                            ?.name?.[
                                                                            locale
                                                                        ]
                                                                    }
                                                                    slug={`/${category?.slug[locale]}`}
                                                                    sort={
                                                                        category?.sort
                                                                    }
                                                                    isSub
                                                                />
                                                            ),
                                                        )}
                                                </SubCategory>
                                            ))}
                                        </>
                                    )}
                            </React.Fragment>
                        ) : (
                            <MenuItem
                                hideProductCount
                                key={item?.id}
                                count={item?.productCount}
                                id={item?.id}
                                name={item?.name[locale]}
                                slug={`/${item?.slug[locale]}`}
                                sort={item?.sort}
                            />
                        ),
                    )}
                </CategoryItemsWrapper>
                {currentUser && name && email ? (
                    <>
                        <ProfileWrapper>
                            <ProfileTitleWrapper href="/account">
                                <Caption lineHeight="18px">{name}</Caption>
                                <Caption size={sizes.xs} lineHeight="22px">
                                    {email}
                                </Caption>
                            </ProfileTitleWrapper>
                        </ProfileWrapper>
                        <FeaturesWrapper>
                            <FeatureItem href="/account/orders">
                                <OrderIcon />
                                <Caption size={sizes.xs}>
                                    {t('myOrders')}
                                </Caption>
                            </FeatureItem>
                            <FeatureItem href="/account/favorites">
                                <FavoriteIcon />
                                <Caption size={sizes.xs}>
                                    {t('favorites')}
                                </Caption>
                            </FeatureItem>
                            <FeatureItem href="/account/coupons">
                                <TicketIcon />
                                <Caption size={sizes.xs}>
                                    {t('myDiscountCoupons')}
                                </Caption>
                            </FeatureItem>
                        </FeaturesWrapper>
                    </>
                ) : (
                    <AuthWrapper>
                        <AuthButton
                            background="primary"
                            color="secondary"
                            onClick={() => router.push('/login')}
                        >
                            {t('userSignIn')}
                        </AuthButton>
                        <AuthButton
                            background="secondary"
                            color="primary"
                            border="1px solid #333333"
                            onClick={() => router.push('/login?tab=register')}
                        >
                            {t('signupNow')}
                        </AuthButton>
                    </AuthWrapper>
                )}
            </MenuContext>
            <MenuFooter>
                <Col>
                    <Caption>{t('followUs')}</Caption>
                    <SocialMediaWrapper>
                        <Link href="https://www.instagram.com/keikei.com/?hl=tr">
                            <InstagramIcon />
                        </Link>
                        <Link href="https://www.facebook.com/KeiKei.co/">
                            <Image
                                src={FacebookIcon}
                                width={24}
                                height={24}
                                alt="Facebook"
                            />
                        </Link>
                    </SocialMediaWrapper>
                </Col>
            </MenuFooter>
        </LeftMenuModal>
    );
};

export default LeftMenu;

const MenuHeader = styled.div`
    padding: 35px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;
const CloseIconWrapper = styled.div`
    cursor: pointer;
`;
const MenuContext = styled.div`
    flex: 1;
    flex-grow: 1;
`;
const CategoryItemsWrapper = styled.div`
    padding: 0 20px 32px 20px;
`;
const ProfileWrapper = styled.div`
    padding: 17px 0;
    margin: 0 20px;
    border-width: 0.5px 0;
    border-style: solid;
    border-color: #e4e4e4;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;
`;
const ProfileTitleWrapper = styled.a`
    display: flex;
    flex-direction: column;
`;
const SubCategory = styled.div`
    padding-left: 10px;
    a {
        padding-left: 10px;
    }

    /* > * {
        &:first-child {
            padding-left: 0;
        }
    } */
`;

const FeaturesWrapper = styled.div`
    padding: 40px 20px;
`;
const FeatureItem = styled.a`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 19px;
    margin-bottom: 22px;
`;
const MenuFooter = styled.div`
    background-color: #f4f4f4;
    padding: 33px 20px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    span {
        margin-bottom: 4px;
    }
`;
const Col = styled.div`
    display: flex;
    flex-direction: column;
`;
const SocialMediaWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
`;
const AuthWrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 15px;
    gap: 24px;
`;
const AuthButton = styled.div`
    cursor: pointer;
    text-align: center;
    padding: 17px 0;

    background-color: ${({ background, theme }) =>
        `${theme.colors[background]}` || `${theme.colors.primary}`};
    color: ${({ color, theme }) =>
        `${theme.colors[color]}` || `${theme.colors.bgGray10}`};
    border: ${({ border }) => border || 'none'};
`;

const ParentWrapper = styled.div`
    padding: 10px 0;
`;

const SubCategoryTitle = styled.div`
    padding: 0 0 10px;
    span {
        font-size: 12px;
    }
`;
